import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { waLink, waMessages } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { name: "", phone: "", email: "", specialization: "", status: "Aktif" };

export default function Teachers() {
  const { user } = useAuth();
  const canManage = user?.role === "administrator";
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/teachers").then((res) => setTeachers(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, phone: t.phone || "", email: t.email || "", specialization: t.specialization || "", status: t.status });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/teachers/${editing.id}`, form);
        toast.success("Data guru diperbarui");
      } else {
        await api.post("/teachers", form);
        toast.success("Guru baru ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan data guru"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Hapus guru "${t.name}"?`)) return;
    try {
      await api.delete(`/teachers/${t.id}`);
      toast.success("Guru dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus guru"));
    }
  };

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Data Guru</h1>
          <p className="mt-1 text-sm text-muted-foreground">{teachers.length} guru/pengajar terdaftar.</p>
        </div>
        {canManage && (
          <Button onClick={openAdd} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-teacher-button">
            <Plus className="h-4 w-4" /> Tambah Guru
          </Button>
        )}
      </div>

      <Card className="border-border shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Spesialisasi</TableHead>
                <TableHead>Telepon</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Jumlah Kelas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teachers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="teachers-empty">
                    Belum ada data guru.
                  </TableCell>
                </TableRow>
              ) : teachers.map((t) => (
                <TableRow key={t.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`teacher-row-${t.id}`}>
                  <TableCell className="font-semibold">{t.name}</TableCell>
                  <TableCell>{t.specialization || "-"}</TableCell>
                  <TableCell>{t.phone || "-"}</TableCell>
                  <TableCell>{t.email || "-"}</TableCell>
                  <TableCell>{t.class_count}</TableCell>
                  <TableCell>
                    <Badge className={t.status === "Aktif" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600 hover:bg-slate-200"}>
                      {t.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {t.phone && (
                      <a
                        href={waLink(t.phone, waMessages.teacher({ name: t.name }))}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`wa-teacher-${t.id}`}
                      >
                        <Button variant="ghost" size="icon" title="Chat WhatsApp guru">
                          <MessageCircle className="h-4 w-4 text-emerald-600" />
                        </Button>
                      </a>
                    )}
                    {canManage && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-teacher-${t.id}`}>
                          <Pencil className="h-4 w-4 text-primary" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`delete-teacher-${t.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="teacher-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Guru" : "Tambah Guru Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Lengkap *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="teacher-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Spesialisasi</Label>
              <Input placeholder="cth: Piano & Keyboard" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} data-testid="teacher-specialization-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telepon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="teacher-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="teacher-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aktif">Aktif</SelectItem>
                    <SelectItem value="Nonaktif">Nonaktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-email-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="teacher-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.name} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="teacher-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
